import { useEffect, useRef, useState } from 'react';
import '../../styles/StrategicChart.css';
import { loadGoogleCharts } from '../../utils/googleCharts';

const numberFormatter = new Intl.NumberFormat('pt-BR', {
    maximumFractionDigits: 1
});

const dateFormatter = new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
});

const shortDateFormatter = new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: 'short'
});

/*
 * Quantidade de pontos interpolados criados ENTRE cada dois
 * pontos reais. Quanto maior, mais "onda" e fluidez na curva.
 * Valores entre 8 e 14 costumam dar um resultado bem orgânico
 * sem pesar demais no desenho.
 */
const SMOOTHING_STEPS = 10;

function formatDate(value, formatter) {
    const date = typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)
        ? new Date(`${value}T00:00:00`)
        : new Date(value);

    return Number.isNaN(date.getTime()) ? '—' : formatter.format(date).replace('.', '');
}

/*
 * Interpolação Catmull-Rom: gera uma curva suave que passa
 * exatamente pelos pontos reais (p1 e p2), usando os vizinhos
 * (p0 e p3) para calcular a tangente da curva. É isso que cria
 * o efeito de "onda" em vez de segmentos retos entre os pontos.
 */
function catmullRom(p0, p1, p2, p3, t) {
    const t2 = t * t;
    const t3 = t2 * t;

    return 0.5 * (
        (2 * p1) +
        (-p0 + p2) * t +
        (2 * p0 - 5 * p1 + 4 * p2 - p3) * t2 +
        (-p0 + 3 * p1 - 3 * p2 + p3) * t3
    );
}

/*
 * Recebe uma série de valores reais e devolve uma série maior,
 * com pontos extras interpolados entre cada par de valores.
 * O ponto interpolado no início de cada segmento (t = 0) é
 * SEMPRE idêntico ao ponto real original — nada é "inventado"
 * no lugar dos dados reais, só entre eles.
 */
function interpolateSeries(values, steps) {
    const result = [];
    const n = values.length;

    if (n < 2) {
        return values.slice();
    }

    for (let i = 0; i < n - 1; i += 1) {
        const p0 = values[i - 1] ?? values[i];
        const p1 = values[i];
        const p2 = values[i + 1];
        const p3 = values[i + 2] ?? values[i + 1];

        for (let step = 0; step < steps; step += 1) {
            const t = step / steps;

            result.push(catmullRom(p0, p1, p2, p3, t));
        }
    }

    result.push(values[n - 1]);

    return result;
}

function StrategicChart({ data, loading = false }) {
    const chartElementRef = useRef(null);
    const tooltipRef = useRef(null);
    // Última medida real do card (ou um valor padrão até a primeira medição).
    const tooltipSizeRef = useRef({ width: 200, height: 104 });
    const [hoveredPoint, setHoveredPoint] = useState(null);

    useEffect(() => {
        if (tooltipRef.current) {
            const { width, height } = tooltipRef.current.getBoundingClientRect();
            if (width && height) tooltipSizeRef.current = { width, height };
        }
    }, [hoveredPoint]);

    useEffect(() => {
        const chartElement = chartElementRef.current;
        const validData = Array.isArray(data)
            ? data.filter(({ date, value, railway, static: staticValue, road }) => (
                date
                && Number.isFinite(value)
                && Number.isFinite(railway)
                && Number.isFinite(staticValue)
                && Number.isFinite(road)

            ))
            : [];

        if (loading || !chartElement || validData.length === 0 || !window.google?.charts) {
            return undefined;
        }

        let chart;
        let mouseOverListener;
        let mouseOutListener;
        let selectListener;

        const drawChart = () => {
            if (
                !chartElementRef.current
                || !window.google?.visualization
                || typeof window.google.visualization.DataTable !== 'function'
                || typeof window.google.visualization.ComboChart !== 'function'
            ) return;

            const steps = validData.length > 1 ? SMOOTHING_STEPS : 1;

            const categories = validData.map(({ date }) => formatDate(date, shortDateFormatter));
            const values = interpolateSeries(validData.map(({ value }) => value), steps);
            const railwayValues = interpolateSeries(validData.map(({ railway }) => railway), steps);
            const staticValues = interpolateSeries(validData.map(({ static: staticValue }) => staticValue), steps);
            const roadValues = interpolateSeries(validData.map(({ road }) => road), steps);

            /*
             * Só os pontos que correspondem a um dado real recebem
             * o label da categoria (data). Os pontos interpolados
             * ficam com categoria vazia para não virarem marcações
             * "fantasma" no eixo X.
             */
            const rowLabels = values.map((_, row) => (
                row % steps === 0 ? categories[row / steps] : ''
            ));

            const dataTable = new window.google.visualization.DataTable();
            dataTable.addColumn('string', 'Data');
            dataTable.addColumn('number', 'Final');
            dataTable.addColumn('number', 'Volume Ferroviário');
            dataTable.addColumn('number', 'Estática');
            dataTable.addColumn('number', 'Volume Rodoviário');
            dataTable.addRows(values.map((_, row) => [
                rowLabels[row],
                values[row],
                railwayValues[row],
                staticValues[row],
                roadValues[row]
            ]));

            chart = chart || new window.google.visualization.ComboChart(chartElementRef.current);
            chart.draw(dataTable, {
                backgroundColor: 'transparent',
                chartArea: { left: 76, right: 24, top: 22, bottom: 52, width: '100%', height: '78%' },
                legend: 'none',
                seriesType: 'line',
                areaOpacity: 0.14,
                series: {
                    0: { type: 'area', color: '#004B9B', areaOpacity: 0.14, lineWidth: 3 },
                    1: { type: 'line', color: '#0090D0', lineWidth: 2 },
                    2: { type: 'line', color: '#DC3545', lineWidth: 2, lineDashStyle: [4, 4] },
                    3: { type: 'line', color: '#2ECC71', lineWidth: 2 }
                },
                curveType: 'function',
                pointSize: 0,
                tooltip: { trigger: 'none' },
                focusTarget: 'category',
                hAxis: {
                    baselineColor: '#C0CCD2',
                    gridlines: { color: 'transparent' },
                    textStyle: { color: '#7B8C96', fontName: 'Open Sans', fontSize: 11 },
                    /*
                     * Multiplicamos por "steps" para que a régua de
                     * labels sempre caia em cima de um ponto real
                     * (múltiplo de "steps"), nunca de um interpolado.
                     */
                    showTextEvery: steps,
                    slantedText: validData.length > 8,
                    slantedTextAngle: 30
                },
                vAxis: {
                    baselineColor: '#C0CCD2',
                    gridlines: { color: '#E8EEF3', count: 5 },
                    minorGridlines: { color: 'transparent' },
                    format: '#,##0.#',
                    textStyle: { color: '#7B8C96', fontName: 'Open Sans', fontSize: 11 }
                },
                enableInteractivity: true,
                animation: { startup: true, duration: 280, easing: 'out' }
            });

            if (mouseOverListener) window.google.visualization.events.removeListener(mouseOverListener);
            if (mouseOutListener) window.google.visualization.events.removeListener(mouseOutListener);
            if (selectListener) window.google.visualization.events.removeListener(selectListener);

            mouseOverListener = window.google.visualization.events.addListener(chart, 'onmouseover', ({ row }) => {
                /*
                 * Os DADOS exibidos usam o ponto real mais próximo
                 * (para não mostrar valores "fantasmas" interpolados),
                 * mas a POSIÇÃO do tooltip usa o "row" do próprio
                 * evento — que é a posição real do mouse na curva
                 * interpolada. É isso que faz o tooltip deslizar
                 * suavemente acompanhando o cursor, em vez de só
                 * pular quando o mouse cruza de um ponto real para
                 * o outro.
                 */
                const nearestRealIndex = Math.min(
                    validData.length - 1,
                    Math.max(0, Math.round(row / steps))
                );

                const point = validData[nearestRealIndex];

                if (!point) return;

                const bounds = chart.getChartLayoutInterface().getBoundingBox(`point#0#${row}`);
                const container = chartElementRef.current.parentElement;
                const containerWidth = container ? container.clientWidth : 0;
                const containerHeight = container ? container.clientHeight : 0;
                const { width: tooltipWidth, height: tooltipHeight } = tooltipSizeRef.current;

                let left = bounds ? bounds.left + 12 : 16;
                let top = bounds ? Math.max(8, bounds.top - 104) : 8;

                // Perto da borda direita, mostra o card do lado esquerdo do ponto.
                if (bounds && containerWidth && left + tooltipWidth + 8 > containerWidth) {
                    left = bounds.left - tooltipWidth - 12;
                }
                left = Math.max(8, left);

                if (containerHeight) {
                    top = Math.min(top, Math.max(8, containerHeight - tooltipHeight - 8));
                }

                setHoveredPoint({
                    point,
                    position: { left, top }
                });
            });
            mouseOutListener = window.google.visualization.events.addListener(chart, 'onmouseout', () => setHoveredPoint(null));

            /*
             * O Google Charts, por padrão, destaca com uma bolinha
             * o ponto mais próximo quando o usuário clica no
             * gráfico (seleção nativa). Como não usamos seleção
             * pra nada, limpamos ela assim que ocorre — isso
             * remove a bolinha "sem função" do clique.
             */
            selectListener = window.google.visualization.events.addListener(chart, 'select', () => {
                chart.setSelection([]);
            });
        };

        loadGoogleCharts().then(drawChart).catch(() => undefined);

        let resizeObserver;

        if (window.ResizeObserver) {
            resizeObserver = new window.ResizeObserver(drawChart);
            resizeObserver.observe(chartElement);
        }

        return () => {
            resizeObserver?.disconnect();
            if (mouseOverListener) window.google.visualization?.events.removeListener(mouseOverListener);
            if (mouseOutListener) window.google.visualization?.events.removeListener(mouseOutListener);
        };
    }, [data, loading]);

    if (loading) {
        return <div className="strategic-chart-state">Carregando dados...</div>;
    }

    if (!Array.isArray(data) || data.length === 0) {
        return <div className="strategic-chart-state">Nenhum dado disponível para o período selecionado.</div>;
    }

    return (
        <div className="flex strategic-chart-canvas">
            <div className="strategic-chart-legend" aria-label="Séries do gráfico">
                <span><i className="strategic-chart-legend-final" />Estoque Final</span>
                <span><i className="strategic-chart-legend-railway" />Volume Ferroviário</span>
                <span><i className="strategic-chart-legend-static" />Estática</span>
                <span><i className="strategic-chart-legend-road" />Volume Rodoviário</span>
            </div>
            <div ref={chartElementRef} className="strategic-chart-canvas-inner" />
            {hoveredPoint && (
                <div ref={tooltipRef} className="strategic-chart-tooltip" style={hoveredPoint.position}>
                    <strong>{formatDate(hoveredPoint.point.date, dateFormatter)}</strong>
                    <span>Estática <b>{numberFormatter.format(hoveredPoint.point.static)}</b></span>
                    <span>Estoque Final<b>{numberFormatter.format(hoveredPoint.point.value)}</b></span>
                    <span>Volume Ferroviário <b>{numberFormatter.format(hoveredPoint.point.railway)}</b></span>
                    <span>Volume Rodoviário <b>{numberFormatter.format(hoveredPoint.point.road)}</b></span>
                </div>
            )}
        </div>
    );
}

export default StrategicChart;